/**
 * Random colours and harmonious palettes.
 *
 * Randomness comes from `crypto.getRandomValues`, so colours are unbiased and cannot be
 * predicted from earlier ones; it is only ever called from an event handler (never during
 * render), because a server-rendered page and its hydration must agree.
 *
 * Harmony rules work on the HSL hue wheel — the RGB colour wheel, in which red 0°, green
 * 120° and blue 240° are equally spaced. Painters' RYB wheels place the "opposite"
 * colours differently, so a complement here is the RGB one (red ↔ cyan).
 *
 *   monochromatic        one hue, lightness spread from dark to light
 *   analogous            neighbours: 0°, +30°, −30°, +60°, −60° from the base
 *   complementary        0° and 180°
 *   split-complementary  0°, +150° and +210°
 *   triadic              0°, +120°, +240°
 *   tetradic (square)    0°, +90°, +180°, +270°
 *   any                  each colour drawn uniformly from the whole RGB cube
 *
 * Asked for more colours than a rule has anchors, a rule cycles through its hues and
 * moves lightness up and down in 14-point steps so repeats stay distinguishable.
 */

import {
  clamp,
  hslToRgb,
  mod360,
  type Rgb,
  rgbToHsl,
} from "@/lib/tool-kit/color-charts/color-space";

export type Rng = () => number;

/** A uniform float in [0, 1) with 53 random bits, from the Web Crypto API. */
export function createCryptoRng(): Rng {
  return () => {
    const words = new Uint32Array(2);
    crypto.getRandomValues(words);
    // 21 bits from the first word + 32 from the second = 53.
    return (
      ((words[0] % 2_097_152) * 4_294_967_296 + words[1]) /
      9_007_199_254_740_992
    );
  };
}

export type Harmony =
  | "any"
  | "monochromatic"
  | "analogous"
  | "complementary"
  | "splitComplementary"
  | "triadic"
  | "tetradic";

export const HARMONIES: readonly Harmony[] = [
  "any",
  "monochromatic",
  "analogous",
  "complementary",
  "splitComplementary",
  "triadic",
  "tetradic",
];

/** Hue offsets in degrees from the base hue, in the order they are handed out. */
export const HARMONY_OFFSETS: Record<
  Exclude<Harmony, "any">,
  readonly number[]
> = {
  monochromatic: [0],
  analogous: [0, 30, -30, 60, -60],
  complementary: [0, 180],
  splitComplementary: [0, 150, 210],
  triadic: [0, 120, 240],
  tetradic: [0, 90, 180, 270],
};

export const MAX_COLORS = 10;
const LIGHTNESS_STEP = 14;

function between(rng: Rng, low: number, high: number): number {
  return low + rng() * (high - low);
}

function roundRgb({ r, g, b }: Rgb): Rgb {
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b), a: 1 };
}

/** A colour drawn uniformly from the sRGB cube (every channel 0–255 equally likely). */
export function randomRgb(rng: Rng): Rgb {
  return {
    r: Math.floor(rng() * 256),
    g: Math.floor(rng() * 256),
    b: Math.floor(rng() * 256),
    a: 1,
  };
}

interface Base {
  hue: number;
  saturation: number;
  lightness: number;
  /** True when the base came from a locked colour, whose own hue is anchor 0. */
  fromLocked: boolean;
}

function chooseBase(locked: readonly (Rgb | null)[], rng: Rng): Base {
  for (const color of locked) {
    if (color) {
      const { h, s, l } = rgbToHsl(color);
      if (s >= 8 && l > 5 && l < 95) {
        return {
          hue: h,
          saturation: clamp(s, 35, 90),
          lightness: clamp(l, 30, 70),
          fromLocked: true,
        };
      }
    }
  }
  return {
    hue: between(rng, 0, 360),
    saturation: between(rng, 50, 85),
    lightness: between(rng, 42, 62),
    fromLocked: false,
  };
}

function monochromaticLightness(slot: number, count: number): number {
  return count === 1 ? 50 : 20 + (65 * slot) / (count - 1);
}

function anchoredColor(
  rule: Exclude<Harmony, "any">,
  base: Base,
  step: number
): Rgb {
  const offsets = HARMONY_OFFSETS[rule];
  const anchor = offsets[step % offsets.length];
  const round = Math.floor(step / offsets.length);
  // Round 0 uses the base lightness; later rounds alternate lighter, darker, lighter…
  const direction = round % 2 === 1 ? 1 : -1;
  const shift =
    round === 0 ? 0 : direction * Math.ceil(round / 2) * LIGHTNESS_STEP;
  return roundRgb(
    hslToRgb({
      h: mod360(base.hue + anchor),
      s: base.saturation,
      l: clamp(base.lightness + shift, 12, 90),
    })
  );
}

/**
 * Build a palette of `count` colours. `locked[i]` is a colour to keep at slot `i`
 * (null to fill it). When any slot is locked, the first locked chromatic colour sets
 * the base hue, so the new colours harmonise with it.
 */
export function generatePalette({
  harmony,
  count,
  locked,
  rng,
}: {
  harmony: Harmony;
  count: number;
  locked: readonly (Rgb | null)[];
  rng: Rng;
}): Rgb[] {
  const size = Math.min(MAX_COLORS, Math.max(1, Math.round(count)));
  if (harmony === "any") {
    return Array.from(
      { length: size },
      (_, slot) => locked[slot] ?? randomRgb(rng)
    );
  }
  const base = chooseBase(locked.slice(0, size), rng);
  let step = base.fromLocked ? 1 : 0;
  return Array.from({ length: size }, (_, slot) => {
    const keep = locked[slot];
    if (keep) {
      return keep;
    }
    if (harmony === "monochromatic") {
      return roundRgb(
        hslToRgb({
          h: base.hue,
          s: base.saturation,
          l: monochromaticLightness(slot, size),
        })
      );
    }
    const color = anchoredColor(harmony, base, step);
    step += 1;
    return color;
  });
}
